import { Dispatch, SetStateAction, useMemo, useRef, useState } from "react"
import { Box, Menu, Stack } from "@mui/material"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faLocationDot, faTruck } from "@fortawesome/pro-light-svg-icons"

import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import {
    fetchMerchantSatellites,
    FetchMerchantSatellitesResponse,
    MerchantQuery,
} from "../../../../../admin/merchantAdminApi"
import type {
    ResourceFilterElement,
    ResourceFilterElementValue,
    ResourceFiltersSelectedItem,
} from "../../../../../../../shared/components/createOrder/createOrderTypes"
import { AddFiltersMenu } from "../../../../../../../shared/components/createOrder/components/resource/resourceFilters/AddFiltersButton"
import { MultiSelectFilter } from "../../../../../../../shared/components/createOrder/components/resource/resourceFilters/MultiSelectFilter"
import { StandardFilterChip } from "../../../../../../../shared/components/filters/StandardFilterChip"
import { MeetingServiceFilterProps } from "../../../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import { DeliveryTypeFilter } from "../../meetingServiceTypes"

// Values must match the keys of the icon map in FilterIcon
enum ProductionFilterId {
    DELIVERY_TYPE = "deliveryType",
    SATELLITES = "satelliteIds",
}

const MULTI_SELECT_ELEMENT_TYPE = "MultiSelect"

const CHIP_ICONS: Record<ProductionFilterId, IconDefinition> = {
    [ProductionFilterId.DELIVERY_TYPE]: faTruck,
    [ProductionFilterId.SATELLITES]: faLocationDot,
}

const SELECTABLE_DELIVERY_TYPES = Object.values(DeliveryTypeFilter).filter((value) => value !== DeliveryTypeFilter.ALL)

function toProductionFilterId(value: string): ProductionFilterId | undefined {
    return Object.values(ProductionFilterId).find((id) => id === value)
}

function toDeliveryTypeFilter(value: string | number): DeliveryTypeFilter | undefined {
    return Object.values(DeliveryTypeFilter).find((deliveryType) => deliveryType === value)
}

const selectSatellites = (data: FetchMerchantSatellitesResponse) => data.satellites

type ProductionFilterElement = ResourceFilterElement & Readonly<{ identifier: ProductionFilterId }>

type Props = Readonly<{
    /** The toolbar filter button the "add filter" list is anchored to; null when that list is closed. */
    addFilterAnchorEl: HTMLElement | null
    onAddFilterClose: () => void
    filtersList: MeetingServiceFilterProps
    setFiltersList: Dispatch<SetStateAction<MeetingServiceFilterProps>>
}>

/**
 * The filter chips plus the two dropdowns behind them: the filter list opened from the toolbar
 * button, and the value picker, which opens on the chip itself - as on the booking timeline, a
 * filter picked from the list appears as a chip with its values already open.
 */
export function ProductionFilterMenu({ addFilterAnchorEl, onAddFilterClose, filtersList, setFiltersList }: Props) {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    // The filter whose values are open, plus the one picked from the list and not given a value yet.
    const [openValuesFilterId, setOpenValuesFilterId] = useState<ProductionFilterId | null>(null)
    const [pendingFilterId, setPendingFilterId] = useState<ProductionFilterId | null>(null)
    // The chip the value picker hangs under. A filter picked from the list gets its chip in the same
    // render, so the anchor is only known once that chip mounts - hence state, not a ref.
    const [valuesAnchorEl, setValuesAnchorEl] = useState<HTMLElement | null>(null)
    const chipRefs = useRef<Partial<Record<ProductionFilterId, HTMLElement | null>>>({})

    const { data: satellites = [] } = useApiQuery(fetchMerchantSatellites, {
        queryName: MerchantQuery.SATELLITES_LIST,
        dependencies: { merchantId },
        select: selectSatellites,
    })

    // The list shows `key` as the label, `value` is what goes to the API
    const filterElements = useMemo<ProductionFilterElement[]>(
        () => [
            {
                identifier: ProductionFilterId.DELIVERY_TYPE,
                elementType: MULTI_SELECT_ELEMENT_TYPE,
                name: l10n.getString("meeting-service-table-delivery-type"),
                translationKey: "meeting-service-table-delivery-type",
                values: SELECTABLE_DELIVERY_TYPES.map((deliveryType) => ({
                    key: l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType),
                    value: deliveryType,
                })),
            },
            {
                identifier: ProductionFilterId.SATELLITES,
                elementType: MULTI_SELECT_ELEMENT_TYPE,
                name: l10n.getString("supplier-meeting-service-drawer-satellites-label"),
                translationKey: "supplier-meeting-service-drawer-satellites-label",
                values: satellites.map((satellite) => ({ key: satellite.name, value: satellite.id })),
            },
        ],
        [l10n, satellites],
    )

    const isValueSelected: Record<ProductionFilterId, (value: ResourceFilterElementValue) => boolean> = {
        [ProductionFilterId.DELIVERY_TYPE]: (value) => value.value === filtersList.deliveryType,
        [ProductionFilterId.SATELLITES]: (value) => Boolean(filtersList.satelliteIds?.includes(String(value.value))),
    }

    // Filters that currently have a value: they carry a chip and a check mark in the filter list.
    const activeFilters: ResourceFiltersSelectedItem[] = filterElements.flatMap((element) => {
        const values = (element.values ?? []).filter(isValueSelected[element.identifier])
        return values.length > 0 ? [{ identifier: element.identifier, values }] : []
    })

    const chipFilters = filterElements.filter(
        (element) =>
            element.identifier === pendingFilterId ||
            activeFilters.some((filter) => filter.identifier === element.identifier),
    )

    const openValuesElement = filterElements.find((element) => element.identifier === openValuesFilterId)
    const openValues = activeFilters.find((filter) => filter.identifier === openValuesFilterId)?.values ?? []

    const clearFilter = (identifier: ProductionFilterId) =>
        setFiltersList((prev) =>
            identifier === ProductionFilterId.DELIVERY_TYPE
                ? { ...prev, deliveryType: DeliveryTypeFilter.ALL }
                : { ...prev, satelliteIds: undefined },
        )

    const openValuesFor = (identifier: ProductionFilterId) => {
        setOpenValuesFilterId(identifier)
        setValuesAnchorEl(chipRefs.current[identifier] ?? null)
    }

    const handleRemoveChip = (identifier: ProductionFilterId) => {
        if (pendingFilterId === identifier) setPendingFilterId(null)
        if (openValuesFilterId === identifier) {
            setOpenValuesFilterId(null)
            setValuesAnchorEl(null)
        }
        clearFilter(identifier)
    }

    // Picking a filter from the list turns it into a chip and opens that chip's values right away.
    const handlePickFilter = (identifier: string) => {
        const filterId = toProductionFilterId(identifier)
        onAddFilterClose()
        if (!filterId) return
        setPendingFilterId(filterId)
        openValuesFor(filterId)
    }

    const handleCloseValues = () => {
        // A filter left without a value never becomes a chip.
        if (pendingFilterId && !activeFilters.some((filter) => filter.identifier === pendingFilterId)) {
            setPendingFilterId(null)
        }
        setOpenValuesFilterId(null)
        setValuesAnchorEl(null)
    }

    const handleToggleValue = (value: ResourceFilterElementValue) => {
        if (openValuesFilterId === ProductionFilterId.DELIVERY_TYPE) {
            const deliveryType = toDeliveryTypeFilter(value.value)
            if (!deliveryType) return
            setFiltersList((prev) => ({
                ...prev,
                deliveryType: prev.deliveryType === deliveryType ? DeliveryTypeFilter.ALL : deliveryType,
            }))
            return
        }
        const satelliteId = String(value.value)
        setFiltersList((prev) => {
            const current = prev.satelliteIds ?? []
            const next = current.includes(satelliteId)
                ? current.filter((id) => id !== satelliteId)
                : [...current, satelliteId]
            return { ...prev, satelliteIds: next.length > 0 ? next : undefined }
        })
    }

    return (
        <>
            {chipFilters.length > 0 && (
                <Stack direction="row" alignItems="center" spacing=".5rem" flexWrap="wrap" useFlexGap>
                    {chipFilters.map((element) => {
                        const values = activeFilters.find((filter) => filter.identifier === element.identifier)?.values
                        return (
                            <Box
                                key={element.identifier}
                                ref={(node: HTMLElement | null) => {
                                    chipRefs.current[element.identifier] = node
                                    // The chip of a just-picked filter mounts after the picker opened.
                                    if (node && openValuesFilterId === element.identifier && valuesAnchorEl !== node) {
                                        setValuesAnchorEl(node)
                                    }
                                }}
                            >
                                <StandardFilterChip
                                    label={values?.map((value) => String(value.key)).join(", ") ?? element.name}
                                    icon={CHIP_ICONS[element.identifier]}
                                    onClick={() => openValuesFor(element.identifier)}
                                    onRemove={() => handleRemoveChip(element.identifier)}
                                />
                            </Box>
                        )
                    })}
                </Stack>
            )}

            <AddFiltersMenu
                anchorEl={addFilterAnchorEl}
                onClose={onAddFilterClose}
                filters={activeFilters}
                resourceFiltersResult={filterElements}
                onAddFilter={handlePickFilter}
            />

            {openValuesElement && valuesAnchorEl && (
                <Menu
                    open
                    anchorEl={valuesAnchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={handleCloseValues}
                >
                    <MultiSelectFilter
                        identifier={openValuesElement.identifier}
                        values={openValuesElement.values ?? []}
                        localValues={openValues}
                        onToggle={handleToggleValue}
                    />
                </Menu>
            )}
        </>
    )
}
